/** Shared query-string helpers for list-page filter ↔ URL sync. */

import type { SortDirection } from "@/types/api";

export function readTrimmedParam(params: URLSearchParams, key: string): string | undefined {
  const value = params.get(key)?.trim();
  return value ? value : undefined;
}

export function readRepeatedTrimmedParams(params: URLSearchParams, key: string): string[] {
  return params.getAll(key).map((id) => id.trim()).filter(Boolean);
}

export function asOptionalBoolean(value: unknown): boolean | undefined {
  if (value === true) return true;
  if (value === false) return false;
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return undefined;
}

export function readOptionalBooleanParam(params: URLSearchParams, key: string): boolean | undefined {
  const raw = params.get(key);
  if (raw === null) return undefined;
  return asOptionalBoolean(raw);
}

export function readOptionalNumberParam(params: URLSearchParams, key: string): number | undefined {
  const raw = readTrimmedParam(params, key);
  if (!raw) return undefined;
  const n = Number(raw);
  return !Number.isNaN(n) && Number.isFinite(n) ? n : undefined;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function readOptionalDateParam(params: URLSearchParams, key: string): string | undefined {
  const value = readTrimmedParam(params, key);
  if (!value || !DATE_RE.test(value)) return undefined;
  return value;
}

export function writeTrimmedParam(next: URLSearchParams, key: string, value: string | undefined) {
  const trimmed = value?.trim();
  if (trimmed) next.set(key, trimmed);
}

export function writeOptionalBooleanParam(next: URLSearchParams, key: string, value: unknown) {
  const parsed = asOptionalBoolean(value);
  if (parsed === true) next.set(key, "true");
  else if (parsed === false) next.set(key, "false");
}

export function writeOptionalNumberParam(next: URLSearchParams, key: string, value: number | undefined) {
  if (value != null && Number.isFinite(value)) next.set(key, String(value));
}

export function writeRepeatedParams(next: URLSearchParams, key: string, values: string[] | undefined) {
  next.delete(key);
  for (const id of values ?? []) {
    const trimmed = id.trim();
    if (trimmed) next.append(key, trimmed);
  }
}

export function deleteQueryKeys(next: URLSearchParams, keys: readonly string[]) {
  for (const key of keys) {
    next.delete(key);
  }
}

/** Query keys for table sort: `?sort=<field>&sort_type=ASC|DESC`. */
const SORT_FIELD_PARAM = "sort";
const SORT_TYPE_PARAM = "sort_type";

/** A resolved sort — both halves always present. */
export interface PageSort {
  sort: string;
  direction: SortDirection;
}

/** `sort_type` is written upper-case but read case-insensitively; anything else is ignored. */
function readSortDirectionParam(params: URLSearchParams): SortDirection | undefined {
  const upper = params.get(SORT_TYPE_PARAM)?.trim().toUpperCase();
  if (upper === "ASC") return "asc";
  if (upper === "DESC") return "desc";
  return undefined;
}

/** Sort from the URL, falling back to `defaults` for whichever half is missing or invalid. */
export function parsePageSortFromSearchParams(params: URLSearchParams, defaults: PageSort): PageSort {
  return {
    sort: readTrimmedParam(params, SORT_FIELD_PARAM) ?? defaults.sort,
    direction: readSortDirectionParam(params) ?? defaults.direction,
  };
}

/** Writes the sort params, or clears them when the sort is the page default. */
export function applyPageSortToSearchParams(
  sort: PageSort,
  current: URLSearchParams,
  defaults: PageSort,
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete(SORT_FIELD_PARAM);
  next.delete(SORT_TYPE_PARAM);
  if (!pageSortEquivalent(sort, defaults)) {
    next.set(SORT_FIELD_PARAM, sort.sort);
    next.set(SORT_TYPE_PARAM, sort.direction.toUpperCase());
  }
  return next;
}

export function pageSortEquivalent(a: PageSort, b: PageSort): boolean {
  return a.sort === b.sort && a.direction === b.direction;
}
